import { useId, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Search, StatusHint } from '@indipia/ui-core';
import { useRecipientSearch } from './hooks/useRecipientSearch';
import type { SearchResultItem } from './hooks/useRecipientSearch';
import { searchCustomers, searchPartners, searchVendors } from './api';
import type { Recipient, RecipientType } from './types';
import { formatPhone, formatPhoneInput } from './utils/phone';

const addSchema = z.object({
  name: z.string().min(1, '이름을 입력해 주세요.'),
  mobile: z
    .string()
    .min(1, '연락처를 입력해 주세요.')
    .regex(/^01[0-9]-\d{3,4}-\d{4}$/, '010-0000-0000 형식으로 입력해 주세요.'),
});
type AddFormValues = z.infer<typeof addSchema>;

interface RecipientAddRowProps {
  type: RecipientType;
  onAdd: (recipient: Omit<Recipient, 'key'>) => void;
  onCancel: () => void;
  /** true 면 고객 외 유형은 등록 파트너 검색 선택만 허용(이름·연락처 직접 입력 숨김) */
  customerOnlyDirectInput?: boolean;
}

export function RecipientAddRow({ type, onAdd, onCancel, customerOnlyDirectInput }: RecipientAddRowProps) {
  const pickOnly = !!customerOnlyDirectInput && type !== 'CUSTOMER';
  const [pickError, setPickError] = useState('');
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const idPrefix = useId();
  const nameId = `${idPrefix}-name`;
  const mobileId = `${idPrefix}-mobile`;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<AddFormValues>({
    resolver: zodResolver(addSchema),
    mode: 'onBlur',
    defaultValues: { name: '', mobile: '' },
  });

  const { keyword, setKeyword, results, isSearching, reset: resetSearch } =
    useRecipientSearch(type);

  function getResultLabel(r: SearchResultItem): string {
    if (r.type === 'CUSTOMER') return `${r.item.customerName} · ${formatPhone(r.item.customerMobile)}`;
    if (r.type === 'PARTNER') return `${r.item.partnerName} · ${formatPhone(r.item.mobile)}`;
    return `${r.item.vendorName}${r.item.contactName ? ` · ${r.item.contactName}` : ''}`;
  }

  // 검색결과 클릭(id 확정)과 직접입력(id 미확정 → 전화번호로 기존 레코드 재확인) 두 경로가
  // 공유하는 실제 추가 로직. selectedId를 상태로 읽으면 방금 setSelectedId한 값이 아직
  // 반영 전이라 stale closure가 되므로, id는 항상 인자로 명시해서 넘긴다.
  async function commitRecipient(name: string, mobile: string, presetId: number | undefined) {
    let resolvedId = presetId;

    if (resolvedId === undefined) {
      const phoneRaw = mobile.replace(/-/g, '');
      try {
        if (type === 'CUSTOMER') {
          const list = await searchCustomers(phoneRaw);
          const found = list.find((r) => r.customerMobile.replace(/-/g, '') === phoneRaw);
          if (found) resolvedId = found.customerId;
        } else if (type === 'PARTNER') {
          const list = await searchPartners(phoneRaw);
          const found = list.find((r) => r.mobile.replace(/-/g, '') === phoneRaw);
          if (found) resolvedId = found.partnerId;
        } else {
          const list = await searchVendors(phoneRaw);
          const found = list.find((r) => r.vendorPhone && r.vendorPhone.replace(/-/g, '') === phoneRaw);
          if (found) resolvedId = found.vendorId;
        }
      } catch {
        // 조회 실패 시 신규로 처리
      }
    }

    onAdd({
      id: resolvedId,
      name: name.trim(),
      mobile: mobile.trim(),
      recipientType: type,
      isNew: resolvedId === undefined,
    });
  }

  // 검색 결과 클릭 = 그 자리에서 바로 추가(확인 버튼 별도 클릭 불필요). id가 이미 확정된
  // 상태라 중복조회 없이 바로 commitRecipient로 넘어간다.
  function handleSelectResult(r: SearchResultItem) {
    let name: string;
    let mobile: string;
    let id: number;
    if (r.type === 'CUSTOMER') {
      name = r.item.customerName;
      mobile = formatPhone(r.item.customerMobile);
      id = r.item.customerId;
    } else if (r.type === 'PARTNER') {
      name = r.item.partnerName;
      mobile = formatPhone(r.item.mobile);
      id = r.item.partnerId;
    } else {
      name = pickOnly ? (r.item.contactName || r.item.vendorName) : (r.item.contactName ?? '');
      mobile = formatPhone(r.item.vendorPhone ?? '');
      id = r.item.vendorId;
    }
    if (pickOnly && !mobile) {
      setPickError('연락처가 등록되지 않아 선택할 수 없습니다.');
      return;
    }
    setPickError('');
    setValue('name', name);
    setValue('mobile', mobile);
    setSelectedId(id);
    clearErrors();
    resetSearch();
    void commitRecipient(name, mobile, id);
  }

  const onConfirm = handleSubmit((data) => commitRecipient(data.name, data.mobile, selectedId));

  // 직접입력(이름/연락처 타이핑) 중 Enter = 확인 버튼과 동일 효과.
  function handleFieldKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    void onConfirm();
  }

  return (
    <div className="space-y-3 rounded-xxl border border-border-default bg-surface-subtle p-4">
      {/* 검색 */}
      <div className="relative">
        <Search
          value={keyword}
          onChange={(v) => {
            setKeyword(v);
            setSelectedId(undefined);
            setPickError('');
          }}
          onKeyDown={(e) => {
            // 검색 결과가 정확히 1건일 때만 Enter로 바로 선택+추가 — 여러 건이면
            // 사용자가 직접 골라야 하니 아무 동작도 하지 않는다.
            if (e.key === 'Enter' && results.length === 1) {
              e.preventDefault();
              handleSelectResult(results[0]);
            }
          }}
          placeholder="이름으로 검색..."
        />
        {isSearching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
            검색 중…
          </span>
        )}
        {results.length > 0 && (
          <ul className="absolute top-full z-20 mt-1 max-h-40 w-full overflow-y-auto rounded-xl border border-border-default bg-surface-raised shadow-lg">
            {results.map((r, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => handleSelectResult(r)}
                  className="w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-subtle"
                >
                  {getResultLabel(r)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pickOnly && (
        <div className="space-y-1">
          <StatusHint tone="muted">등록된 {type === 'PARTNER' ? '반장님' : '거래처'}만 선택할 수 있습니다.</StatusHint>
          {pickError && <p className="text-xs text-error">{pickError}</p>}
        </div>
      )}

      {/* 이름 */}
      {!pickOnly && (
      <div className="space-y-1">
        <label htmlFor={nameId} className="text-xs font-medium text-text-secondary">
          {type === 'VENDOR' ? '담당자명' : '이름'}
        </label>
        <input
          id={nameId}
          type="text"
          placeholder="이름 입력"
          {...register('name')}
          onKeyDown={handleFieldKeyDown}
          className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary-400 ${
            errors.name ? 'border-error' : 'border-border-default'
          }`}
        />
        {errors.name && <p className="text-xs text-error">{errors.name.message}</p>}
      </div>
      )}

      {/* 연락처 */}
      {!pickOnly && (
      <div className="space-y-1">
        <label htmlFor={mobileId} className="text-xs font-medium text-text-secondary">연락처</label>
        <Controller
          name="mobile"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              id={mobileId}
              type="tel"
              placeholder="010-0000-0000"
              onChange={(e) => field.onChange(formatPhoneInput(e.target.value))}
              onKeyDown={handleFieldKeyDown}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary-400 ${
                errors.mobile ? 'border-error' : 'border-border-default'
              }`}
            />
          )}
        />
        {errors.mobile && <p className="text-xs text-error">{errors.mobile.message}</p>}
      </div>
      )}

      {/* 버튼 */}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" size="sm" type="button" onClick={onCancel}>
          {pickOnly ? '닫기' : '취소'}
        </Button>
        {!pickOnly && (
          <Button variant="primary" size="sm" type="button" onClick={() => void onConfirm()}>
            확인
          </Button>
        )}
      </div>
    </div>
  );
}
